import type { UserEntry } from "types/Group";

type ParticipantWithId = Pick<UserEntry, "id"> & Partial<UserEntry>;

function participantScore(person: ParticipantWithId): number {
    let score = 0;
    if (person.active !== false) score += 16;
    if (person.isMock === false) score += 8;
    if (person.userId) score += 4;
    if (person.username) score += 2;
    if (person.alias) score += 2;
    if (person.inviteToken) score += 1;
    if (person.displayName?.trim()) score += 1;
    return score;
}

function mergePeople(current: UserEntry, incoming: UserEntry): UserEntry {
    const preferred = participantScore(incoming) >= participantScore(current) ? incoming : current;
    const fallback = preferred === incoming ? current : incoming;

    return {
        ...fallback,
        ...preferred,
        id: preferred.id || fallback.id,
        displayName: preferred.displayName?.trim() || fallback.displayName?.trim() || preferred.id || fallback.id,
        isMock: preferred.isMock === false || fallback.isMock === false ? false : Boolean(preferred.isMock),
        active: preferred.active !== false || fallback.active !== false ? true : false,
        alias: preferred.alias || fallback.alias,
        username: preferred.username || fallback.username,
        userId: preferred.userId || fallback.userId,
        inviteToken: preferred.inviteToken || fallback.inviteToken,
    };
}

export function dedupePeopleById(people: UserEntry[] = []): UserEntry[] {
    const byId = new Map<string, UserEntry>();
    const withoutId: UserEntry[] = [];

    for (const person of people) {
        const id = String(person?.id ?? "").trim();
        if (!id) {
            withoutId.push(person);
            continue;
        }

        const normalizedPerson = { ...person, id };
        const current = byId.get(id);
        byId.set(id, current ? mergePeople(current, normalizedPerson) : normalizedPerson);
    }

    return [...byId.values(), ...withoutId];
}

export function activePeopleFor(people: UserEntry[] = []): UserEntry[] {
    return dedupePeopleById(people).filter((person) => person.active !== false);
}

export function participantDisplayName(person: Pick<UserEntry, "displayName"> & Partial<UserEntry>): string {
    return person.displayName?.trim() || person.alias?.trim() || person.username?.trim() || person.id || "Persona";
}

export function createParticipantDisplayNameResolver(people: UserEntry[], fallback: string): (personId?: string) => string {
    return (personId?: string) => {
        if (!personId) return fallback;
        const person = people.find((entry) => entry.id === personId);
        return person ? participantDisplayName(person) : fallback;
    };
}

export function resolveCurrentParticipantId(username: string, userId: string | undefined, people: UserEntry[]): string {
    const current = people.find((person) => (
        person.userId === userId ||
        person.id === userId ||
        person.username === username ||
        (!person.userId && person.id === username)
    ));
    return current?.id ?? userId ?? username;
}
